import React from 'react';

import { Select } from './Select';
import { BaseSwitch } from './BaseSwitch';
import { useSelect } from './Select';
import { useSwitch } from './BaseSwitch';
import { InfraDependenciesData } from '../types/formTypes';

interface InfraDependenciesSectionProps {
    formRowStyle: React.CSSProperties;
    onValidationChange?: (isValid: boolean) => void;
    onDataChange?: (data: InfraDependenciesData) => void;
    databaseOptions?: Array<{ content: string; value: string }>;
    layout?: 'table' | 'card';
}

export interface InfraDependenciesSectionRef {
    validateInfraDependencies: () => boolean;
    resetInfraDependencies: () => void;
}

export const InfraDependenciesSection = React.forwardRef<InfraDependenciesSectionRef, InfraDependenciesSectionProps>(({
    formRowStyle,
    onValidationChange,
    onDataChange,
    databaseOptions: propDatabaseOptions,
    layout = 'table'
}, ref) => {
    // Database options - use prop if provided, otherwise fallback to hardcoded options
    const databaseOptions = propDatabaseOptions || [
        {
            content: "MySQL",
            value: "MYSQL"
        },
        {
            content: "PostgreSQL",
            value: "POSTGRES"
        },
        {
            content: "None",
            value: "NONE"
        }
    ];

    // Handle all Infra Dependencies state internally
    const {
        value: database,
        error: databaseError,
        handleChange: handleDatabaseChangeRaw,
        validate: validateDatabase,
        setValue: setDatabase,
    } = useSelect({
        options: databaseOptions,
        required: true,
        requiredMessage: 'Database is required',
        invalidSelectionMessage: 'Please select a valid database',
        defaultValue: 'NONE' // Set "None" as the default value
    });

    // Adapt handler to PatternFly signature
    const handleDatabaseChange = React.useCallback((
        _event: React.MouseEvent<Element, MouseEvent> | undefined, 
        value: string | number | undefined
    ) => {
        const stringValue = typeof value === 'string' ? value : (value?.toString() || '');
        handleDatabaseChangeRaw(stringValue);
    }, [handleDatabaseChangeRaw]);

    const {
        value: kafka,
        handleChange: handleKafkaChange,
        setValue: setKafka
    } = useSwitch({
        defaultValue: false // Default off
    });

    const {
        value: redis,
        handleChange: handleRedisChange,
        setValue: setRedis
    } = useSwitch({
        defaultValue: false // Default off
    });

    // Notify parent of validation status changes
    React.useEffect(() => {
        // Database selection is required (including "NONE" option)
        const isValid = !databaseError && !!database;
        onValidationChange?.(isValid);
    }, [databaseError, database, onValidationChange]);

    // Notify parent of data changes
    React.useEffect(() => {
        const infraData: InfraDependenciesData = {
            database: database || undefined,
            kafka,
            redis
        };
        onDataChange?.(infraData);
    }, [database, kafka, redis, onDataChange]);

    // Expose validation and reset functions for form submission
    React.useImperativeHandle(ref, () => ({
        validateInfraDependencies: () => {
            // Database selection is required (including "NONE" option)
            validateDatabase();
            return !databaseError && !!database;
        },
        resetInfraDependencies: () => {
            setDatabase('NONE'); // Reset to "None" default value
            setKafka(false); // Reset to default off state
            setRedis(false); // Reset to default off state
        }
    }));

    if (layout === 'card') {
        return (
            <div className="generator-form">
                <div className="form-section" style={{ borderBottom: 'none', marginBottom: 0, paddingBottom: 0 }}>
                    <h3 className="form-section-title">🏗️ Infrastructure</h3>

                    <div className="form-group">
                        <label className="form-label">Database</label>
                        <Select
                            key={`database-${database}`}
                            options={databaseOptions}
                            initialValue={database}
                            onSelect={handleDatabaseChange}
                            validated={databaseError ? 'error' : 'default'}
                            required={true}
                            placeholder="Select database"
                        />
                        {databaseError && (
                            <div className="dx-helper-text dx-helper-text--error">{databaseError}</div>
                        )}
                    </div>

                    <div className="form-toggles">
                        <div className="toggle-item">
                            <div>
                                <span className="toggle-text">Kafka</span>
                                <div className="toggle-desc">Event streaming</div>
                            </div>
                            <BaseSwitch
                                isChecked={kafka}
                                onChange={handleKafkaChange}
                                label=""
                                id="kafka-switch"
                                ouiaId="KafkaSwitch"
                            />
                        </div>
                        <div className="toggle-item">
                            <div>
                                <span className="toggle-text">Redis</span>
                                <div className="toggle-desc">Caching</div>
                            </div>
                            <BaseSwitch
                                isChecked={redis}
                                onChange={handleRedisChange}
                                label=""
                                id="redis-switch"
                                ouiaId="RedisSwitch"
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="form-group">
                <label className="form-label" style={{ fontWeight: 'bold', fontSize: '18px' }}>Infra Dependencies</label>
            </div>
            <div className="form-group">
                <label className="form-label">Database</label>
                <Select
                    key={`database-${database}`}
                    options={databaseOptions}
                    initialValue={database}
                    onSelect={handleDatabaseChange}
                    validated={databaseError ? 'error' : 'default'}
                    required={true}
                    placeholder="Select database"
                />
                {databaseError && (
                    <div className="dx-helper-text dx-helper-text--error">{databaseError}</div>
                )}
            </div>
            <div className="form-group">
                <label className="form-label">Kafka</label>
                <BaseSwitch
                    isChecked={kafka}
                    onChange={handleKafkaChange}
                    label="Kafka"
                    id="kafka-switch"
                    ouiaId="KafkaSwitch"
                />
            </div>
            <div className="form-group">
                <label className="form-label">Redis</label>
                <BaseSwitch
                    isChecked={redis}
                    onChange={handleRedisChange}
                    label="Redis"
                    id="redis-switch"
                    ouiaId="RedisSwitch"
                />
            </div>
        </>
    );
});

// Export a hook for the parent to access validation functions
export const useInfraDependenciesValidation = () => {
    const infraDependenciesRef = React.useRef<InfraDependenciesSectionRef>(null);

    const validateInfraDependencies = React.useCallback(() => {
        return infraDependenciesRef.current?.validateInfraDependencies() ?? true;
    }, []);

    const resetInfraDependencies = React.useCallback(() => {
        infraDependenciesRef.current?.resetInfraDependencies();
    }, []);

    return {
        infraDependenciesRef,
        validateInfraDependencies,
        resetInfraDependencies
    };
};
